import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../../assets/images/logos/logo_white.svg';
import { AppContext } from '../../AppContext';
import { IRouteModel } from '../../Routes';
import ListItemLink from './ListItemLink';
import {
  useCompanyCreatedSubscription,
  useCompanyUpdatedSubscription,
  useCompanyDeletedSubscription,
  useContactDeletedSubscription,
  useContactCreatedSubscription,
  useMembershipUpdatedSubscription,
  ICompany,
  IContact,
  useContactUpdatedSubscription,
  useProductDeletedSubscription,
  useProductCreatedSubscription,
  IProduct,
  useProductUpdatedSubscription,
  useMembershipCreatedSubscription,
  IMembership,
  useMembershipDeletedSubscription,
  useCommentAddedSubscription,
  useCommentDeletedSubscription,
  useInvoiceUpdatedSubscription,
  useInvoiceDeletedSubscription,
  IInvoice,
  useInvoiceCreatedSubscription,
  useUserUpdatedSubscription,
  useUserDeletedSubscription,
  useUserCreatedSubscription,
  IUser,
  useUserRoleRemovedSubscription,
  useInterestDeletedSubscription,
  IInterest,
  useTemplateDeletedSubscription,
  useTemplateCreatedSubscription,
  ITemplate,
  useTemplateUpdatedSubscription,
  useRecipientListDeletedSubscription,
  useRecipientListCreatedSubscription,
  useRecipientListUpdatedSubscription,
  IRecipientList,
  useSchedulerDeletedSubscription,
  useSchedulerCreatedSubscription,
  useSchedulerUpdatedSubscription,
  IScheduler,
  useEventDeletedSubscription,
  useEventCreatedSubscription,
  ICalendarEvent,
  useEventUpdatedSubscription,
  useStockCreatedSubscription,
  IStock,
  useIndexCreatedSubscription,
  IIndex,
  useIndexDeletedSubscription,
  useIndexUpdatedSubscription,
  useIndexSerieDeletedSubscription,
  IIndexSerie,
  useIndexSerieCreatedSubscription,
  useIndexSerieUpdatedSubscription,
  useEtfCreatedSubscription,
  useEtfUpdatedSubscription,
  IEtf,
  useEtfDeletedSubscription,
  IFtseFile,
  useFtseFileCreatedSubscription,
  useFtseFileDeletedSubscription,
  useFtseFileUpdatedSubscription,
  useFtseConfigurationUpdatedSubscription,
  IFtseConfiguration,
  useSiteSectionCreatedSubscription,
  ISiteSection,
  useSiteSectionDeletedSubscription,
  useSiteSectionUpdatedSubscription,
  useDocumentDeletedSubscription,
  IDocument,
  useDocumentUpdatedSubscription,
  useDocumentCreatedSubscription
} from '../../generated/graphql';
import { Actions } from '../../enums/ActionEnums';
import { Colors } from '../../utils/theme';
import {
  addCompanyToCache,
  removeCompanyFromCache,
  updateCompanyInCache,
  removeContactFromCache,
  addContactToCache,
  updateContactInCache,
  addProductToCache,
  updateProductInCache,
  removeProductFromCache,
  addMembershipToCache,
  updateMembershipInCache,
  removeMembershipFromCache,
  addInvoiceInCache,
  removeInvoiceFromCache,
  updateInvoiceInCache,
  updateUserInCache,
  removeUserFromCache,
  createUserToCache,
  remoteInterestInCache,
  removeTemplateFromCache,
  addTemplateInCache,
  updateTemplateInCache,
  removeRecipientListFromCache,
  addRecipientListInCache,
  updateRecipientListInCache,
  removeSchedulerFromCache,
  addSchedulerToCache,
  updateSchedulerInCache,
  removeEventFromCache,
  addEventToCache,
  updateEventInCache,
  addStockInCache,
  addIndexInCache,
  removeIndexFromCache,
  updateIndexInCache,
  addIndexSerieInCache,
  updateIndexSerieInCache,
  removeIndexSerieFromCache,
  addEtfInCache,
  updateEtfInCache,
  removeEtfFromChache,
  addFtseFileInCache,
  removeFtseFileFromCache,
  updateFtseFileInCache,
  updateFtseConfigurationInCache,
  addSiteSectionInCache,
  removeSiteSectionFromCache,
  updateSiteSectionInCache,
  removeDocumentFromCache,
  updateDocumentInCache,
  addDocumentInCache
} from '../../utils/clientUtils';
import { AppConsts } from '../../enums/AppConsts';
import { Box, Drawer, List, ListItem, Theme, Tooltip, CSSObject } from '@mui/material';
import { useApolloClient } from '@apollo/react-hooks';
import { ExitToApp } from '@mui/icons-material';

const openDrawerWidth = 180;
const closedDrawerWidth = 60;

const openedMixin = (theme: Theme): CSSObject => ({
  width: openDrawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  background: `linear-gradient(${theme.palette.primary.main}, ${theme.palette.primary.light})`,
  overflowX: 'hidden',
  border: 'none'
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  background: `linear-gradient(${theme.palette.primary.main}, ${theme.palette.primary.light})`,
  overflowX: 'hidden',
  width: closedDrawerWidth,
  border: 'none'
});

const Navigation: React.FunctionComponent = () => {
  const { state, dispatch } = useContext(AppContext);
  const client = useApolloClient();
  const createCompanySubscription = useCompanyCreatedSubscription();
  const updateCompanySubscription = useCompanyUpdatedSubscription();
  const deleteCompanySubscription = useCompanyDeletedSubscription();
  const createContactSubscription = useContactCreatedSubscription();
  const updateContactSubscription = useContactUpdatedSubscription();
  const deleteContactSubscription = useContactDeletedSubscription();
  const createProductSubscription = useProductCreatedSubscription();
  const updateProductSubscription = useProductUpdatedSubscription();
  const deleteProductSubscription = useProductDeletedSubscription();
  const deleteMembershipSubscription = useMembershipDeletedSubscription();
  const createMembershipSubscription = useMembershipCreatedSubscription();
  const updateMembershipSubscription = useMembershipUpdatedSubscription();
  const commentAddedSubscription = useCommentAddedSubscription();
  const commentDeletedSubscription = useCommentDeletedSubscription();
  const createInvoiceSubscription = useInvoiceCreatedSubscription();
  const updateInvoiceSubscription = useInvoiceUpdatedSubscription();
  const deleteInvoiceSubscription = useInvoiceDeletedSubscription();
  const updateUserSubscription = useUserUpdatedSubscription();
  const deleteUserSubscription = useUserDeletedSubscription();
  const createUserSubscription = useUserCreatedSubscription();
  const userRoleRemovedSubscription = useUserRoleRemovedSubscription();
  const deleteInterestSubscription = useInterestDeletedSubscription();
  const deleteTemplateSubscription = useTemplateDeletedSubscription();
  const createTemplateSubscription = useTemplateCreatedSubscription();
  const updateTemplateSubscription = useTemplateUpdatedSubscription();
  const deleteRecipientListSubscription = useRecipientListDeletedSubscription();
  const createRecipientListSubscription = useRecipientListCreatedSubscription();
  const updateRecipientListSubscription = useRecipientListUpdatedSubscription();
  const createSchedulerSubscription = useSchedulerCreatedSubscription();
  const deleteSchedulerSubscription = useSchedulerDeletedSubscription();
  const updateSchedulerSubscription = useSchedulerUpdatedSubscription();
  const deleteEventSubscription = useEventDeletedSubscription();
  const createdEventSubscription = useEventCreatedSubscription();
  const updateEventSubscription = useEventUpdatedSubscription();
  const createStockSubscription = useStockCreatedSubscription();
  const createIndexSubscription = useIndexCreatedSubscription();
  const deleteIndexSubscription = useIndexDeletedSubscription();
  const updateIndexSubscription = useIndexUpdatedSubscription();
  const deleteIndexSerieSubscription = useIndexSerieDeletedSubscription();
  const createIndexSerieSubscription = useIndexSerieCreatedSubscription();
  const updateIndexSerieSubscription = useIndexSerieUpdatedSubscription();
  const createEtfSubscription = useEtfCreatedSubscription();
  const updateEtfSubscription = useEtfUpdatedSubscription();
  const deleteEtfSubscription = useEtfDeletedSubscription();
  const createFtseFileSubscription = useFtseFileCreatedSubscription();
  const deleteFtseFileSubscription = useFtseFileDeletedSubscription();
  const updateFtseFileSubscription = useFtseFileUpdatedSubscription();
  const updateFtseConfigurationSubscription = useFtseConfigurationUpdatedSubscription();
  const createSiteSectionSubscription = useSiteSectionCreatedSubscription();
  const deleteSiteSectionSubscription = useSiteSectionDeletedSubscription();
  const updateSiteSectionSubscription = useSiteSectionUpdatedSubscription();
  const deleteDocumentSubscription = useDocumentDeletedSubscription();
  const updateDocumentSubscription = useDocumentUpdatedSubscription();
  const createDocumentSubscription = useDocumentCreatedSubscription();

  const appRoutes: IRouteModel[] = [];
  state.routes.forEach((r) => {
    r.subRoutes ? r.subRoutes.forEach((sr) => appRoutes.push(sr)) : appRoutes.push(r);
  });

  const routes = appRoutes
    .filter((r: IRouteModel) => r.showInNav)
    .map((re: IRouteModel) => re.path);

  // const classes = useStyles();
  const [open, setOpen] = useState(true);
  const [prevOpenState, setPrevOpenState] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const locationElements = location.pathname.split('/').filter((e) => e);
  const locationName = '/' + locationElements[0];
  const routeIndex = routes.indexOf(locationName) < 0 ? 0 : routes.indexOf(locationName);
  const [selected, setSelected] = useState<string>(routes[routeIndex] as string);

  useEffect(() => {
    if (createCompanySubscription.data?.result) {
      addCompanyToCache(createCompanySubscription.data.result as ICompany);
    }
    if (updateCompanySubscription.data?.result) {
      updateCompanyInCache(updateCompanySubscription.data?.result as ICompany);
    }
  }, [createCompanySubscription.data, updateCompanySubscription.data]);

  useEffect(() => {
    if (deleteCompanySubscription.data?.result) {
      removeCompanyFromCache(deleteCompanySubscription.data.result as ICompany);
    }
  }, [deleteCompanySubscription.data]);

  useEffect(() => {
    if (deleteContactSubscription.data?.result) {
      removeContactFromCache(deleteContactSubscription.data.result as IContact);
    }
  }, [deleteContactSubscription]);

  useEffect(() => {
    if (createTemplateSubscription.data?.result) {
      addTemplateInCache(createTemplateSubscription.data.result as ITemplate);
    }
    if (updateTemplateSubscription.data?.result) {
      updateTemplateInCache(updateTemplateSubscription.data?.result as ITemplate);
    }
  }, [createTemplateSubscription, updateTemplateSubscription]);

  useEffect(() => {
    if (deleteTemplateSubscription.data?.result) {
      removeTemplateFromCache(deleteTemplateSubscription.data.result as ITemplate);
    }
  }, [deleteTemplateSubscription]);

  useEffect(() => {
    if (deleteMembershipSubscription.data?.result) {
      removeMembershipFromCache(deleteMembershipSubscription.data.result as IMembership);
    }
  }, [deleteMembershipSubscription]);

  useEffect(() => {
    if (createContactSubscription.data?.result) {
      addContactToCache(createContactSubscription.data?.result as IContact);
    }
    if (updateContactSubscription.data?.result) {
      updateContactInCache(updateContactSubscription.data.result as IContact);
    }
  }, [createContactSubscription.data, updateContactSubscription.data]);

  useEffect(() => {
    if (createDocumentSubscription.data?.result) {
      addDocumentInCache(createDocumentSubscription.data?.result as IDocument);
    }
    if (updateDocumentSubscription.data?.result) {
      updateDocumentInCache(updateDocumentSubscription.data.result as IDocument);
    }
    if (deleteDocumentSubscription.data?.result) {
      removeDocumentFromCache(deleteDocumentSubscription.data?.result as IDocument);
    }
  }, [deleteDocumentSubscription, updateDocumentSubscription, createDocumentSubscription]);

  useEffect(() => {
    if (createProductSubscription.data?.result) {
      addProductToCache(createProductSubscription.data.result as IProduct);
    }
    if (updateProductSubscription.data?.result) {
      updateProductInCache(updateProductSubscription.data.result as IProduct);
    }
    if (deleteProductSubscription.data?.result) {
      removeProductFromCache(deleteProductSubscription.data.result as IProduct);
    }
  }, [
    createProductSubscription.data,
    updateProductSubscription.data,
    deleteProductSubscription.data
  ]);

  useEffect(() => {
    if (createMembershipSubscription.data?.result) {
      addMembershipToCache(createMembershipSubscription.data.result as IMembership);
    }
    if (updateMembershipSubscription.data?.result) {
      updateMembershipInCache(updateMembershipSubscription.data?.result as IMembership);
    }
    if (commentAddedSubscription.data?.result) {
      updateMembershipInCache(commentAddedSubscription.data?.result as IMembership);
    }
    if (commentDeletedSubscription.data) {
      updateMembershipInCache(commentDeletedSubscription.data?.result as IMembership);
    }
  }, [
    createMembershipSubscription.data,
    updateMembershipSubscription.data,
    commentAddedSubscription.data,
    commentDeletedSubscription.data
  ]);

  useEffect(() => {
    if (createInvoiceSubscription.data?.result) {
      addInvoiceInCache(createInvoiceSubscription.data?.result as IInvoice);
    }
    if (updateInvoiceSubscription.data?.result) {
      updateInvoiceInCache(updateInvoiceSubscription.data?.result as IInvoice);
    }
    if (deleteInvoiceSubscription.data?.result) {
      removeInvoiceFromCache(deleteInvoiceSubscription.data?.result as IInvoice);
    }
  }, [
    createInvoiceSubscription.data,
    updateInvoiceSubscription.data,
    deleteInvoiceSubscription.data
  ]);

  useEffect(() => {
    if (createUserSubscription.data?.result) {
      createUserToCache(createUserSubscription.data.result as IUser);
    }
    if (updateUserSubscription.data?.result) {
      updateUserInCache(updateUserSubscription.data?.result);
    }
    if (deleteUserSubscription.data?.result) {
      removeUserFromCache(deleteUserSubscription.data.result as IUser);
    }
  }, [updateUserSubscription.data, createUserSubscription.data, deleteUserSubscription.data]);

  useEffect(() => {
    if (userRoleRemovedSubscription.data?.result) {
      //Works faster than updateUserInCache
      createUserToCache(userRoleRemovedSubscription.data?.result);
    }
  }, [userRoleRemovedSubscription.data]);

  useEffect(() => {
    if (deleteInterestSubscription.data?.result) {
      remoteInterestInCache(deleteInterestSubscription.data.result as IInterest);
    }
  }, [deleteInterestSubscription.data]);

  useEffect(() => {
    if (deleteSchedulerSubscription.data?.result) {
      removeSchedulerFromCache(deleteSchedulerSubscription.data?.result.id);
    }
    if (createSchedulerSubscription.data?.result) {
      addSchedulerToCache(createSchedulerSubscription.data?.result as IScheduler);
    }
    if (updateSchedulerSubscription.data?.result) {
      updateSchedulerInCache(updateSchedulerSubscription.data?.result as IScheduler);
    }
  }, [
    createSchedulerSubscription.data,
    deleteSchedulerSubscription.data,
    updateSchedulerSubscription.data
  ]);

  useEffect(() => {
    if (createdEventSubscription.data?.result) {
      addEventToCache(createdEventSubscription.data?.result as ICalendarEvent);
    }
    if (updateEventSubscription.data?.result) {
      updateEventInCache(updateEventSubscription.data?.result as ICalendarEvent);
    }
    if (deleteEventSubscription.data?.result) {
      removeEventFromCache(deleteEventSubscription.data?.result.id);
    }
  }, [deleteEventSubscription.data, createdEventSubscription.data, updateEventSubscription.data]);

  useEffect(() => {
    if (deleteRecipientListSubscription.data?.result) {
      removeRecipientListFromCache(deleteRecipientListSubscription.data?.result.id);
    }
    if (createRecipientListSubscription.data?.result) {
      addRecipientListInCache(createRecipientListSubscription.data?.result as IRecipientList);
    }
    if (updateRecipientListSubscription.data?.result) {
      updateRecipientListInCache(updateRecipientListSubscription.data?.result as IRecipientList);
    }
  }, [
    deleteRecipientListSubscription.data,
    createRecipientListSubscription.data,
    updateRecipientListSubscription.data
  ]);

  useEffect(() => {
    if (createStockSubscription.data?.result) {
      addStockInCache(createStockSubscription.data?.result as IStock);
    }
  }, [createStockSubscription.data]);

  useEffect(() => {
    if (createIndexSubscription.data?.result) {
      addIndexInCache(createIndexSubscription.data.result as IIndex);
    }
    if (deleteIndexSubscription.data?.result) {
      removeIndexFromCache(deleteIndexSubscription.data?.result as IIndex);
    }
    if (updateIndexSubscription.data?.result) {
      updateIndexInCache(updateIndexSubscription.data?.result as IIndex);
    }
  }, [createIndexSubscription.data, deleteIndexSubscription.data, updateIndexSubscription.data]);

  useEffect(() => {
    if (createIndexSerieSubscription.data?.result) {
      addIndexSerieInCache(createIndexSerieSubscription.data.result as IIndexSerie);
    }
    if (updateIndexSerieSubscription.data?.result) {
      updateIndexSerieInCache(updateIndexSerieSubscription.data.result as IIndexSerie);
    }
    if (deleteIndexSerieSubscription.data?.result) {
      removeIndexSerieFromCache(deleteIndexSerieSubscription.data?.result as IIndexSerie);
    }
  }, [
    deleteIndexSerieSubscription.data,
    createIndexSerieSubscription,
    updateIndexSerieSubscription
  ]);

  useEffect(() => {
    if (createFtseFileSubscription.data?.result) {
      addFtseFileInCache(createFtseFileSubscription.data.result as IFtseFile);
    }
    if (deleteFtseFileSubscription.data?.result) {
      removeFtseFileFromCache(deleteFtseFileSubscription.data.result as IFtseFile);
    }
    if (updateFtseFileSubscription.data?.result) {
      updateFtseFileInCache(updateFtseFileSubscription.data.result as IFtseFile);
    }
  }, [
    createFtseFileSubscription.data,
    deleteFtseFileSubscription.data,
    updateFtseFileSubscription.data
  ]);

  useEffect(() => {
    if (createEtfSubscription.data?.result) {
      addEtfInCache(createEtfSubscription.data.result as IEtf);
    }
    if (updateEtfSubscription.data?.result) {
      updateEtfInCache(updateEtfSubscription.data.result as IEtf);
    }
    if (deleteEtfSubscription.data?.result) {
      removeEtfFromChache(deleteEtfSubscription.data?.result as IEtf);
    }
  }, [createEtfSubscription, updateEtfSubscription, deleteEtfSubscription]);

  useEffect(() => {
    if (createSiteSectionSubscription.data?.result) {
      addSiteSectionInCache(createSiteSectionSubscription.data.result as ISiteSection);
    }
    if (updateSiteSectionSubscription.data?.result) {
      updateSiteSectionInCache(updateSiteSectionSubscription.data.result as ISiteSection);
    }
    if (deleteSiteSectionSubscription.data?.result) {
      removeSiteSectionFromCache(deleteSiteSectionSubscription.data?.result as ISiteSection);
    }
  }, [createSiteSectionSubscription, updateSiteSectionSubscription, deleteSiteSectionSubscription]);

  useEffect(() => {
    if (updateFtseConfigurationSubscription.data?.result) {
      updateFtseConfigurationInCache(
        updateFtseConfigurationSubscription.data.result as IFtseConfiguration
      );
    }
  }, [updateFtseConfigurationSubscription]);

  useEffect(() => {
    if (createProductSubscription.data?.result) {
      addProductToCache(createProductSubscription.data.result as IProduct);
    }
    if (updateProductSubscription.data?.result) {
      updateProductInCache(updateProductSubscription.data.result as IProduct);
    }
    if (deleteProductSubscription.data?.result) {
      removeProductFromCache(deleteProductSubscription.data.result as IProduct);
    }
  }, [
    createProductSubscription.data,
    updateProductSubscription.data,
    deleteProductSubscription.data
  ]);

  if (routes[routeIndex] !== selected) {
    setSelected(routes[routeIndex] as string);
  }

  if (routes[routeIndex] !== selected) {
    setSelected(routes[routeIndex] as string);
  }

  const handleDrawerMove = () => {
    if (prevOpenState == open) {
      setPrevOpenState(!open);
      setOpen(open);
    } else {
      setPrevOpenState(open);
      setOpen(!open);
    }
  };

  const handleSelectedChanged = (path: string) => {
    navigate(path);
    setSelected(path);
  };

  const handleLogout = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    client.cache.reset();
    localStorage.removeItem(AppConsts.AccessToken);
    dispatch({ type: Actions.RemoveUser });
    // history('/login');
  };

  const onHover = (isEnter: boolean) => {
    if (isEnter) {
      if (!open) {
        setOpen(true);
      }
    } else {
      if (prevOpenState) {
        setOpen(false);
      }
    }
  };

  return state.user.data ? (
    <Box
      sx={{ display: 'flex' }}
      id={'navigation-container'}
      //className={classes.root}
      onClick={handleDrawerMove}
      onMouseLeave={() => onHover(false)}
      onMouseEnter={() => onHover(true)}
    >
      <Drawer
        variant="permanent"
        sx={(theme) => ({
          width: openDrawerWidth,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
          ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme)
          }),
          ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme)
          })
        })}
      >
        <Box
          sx={{ height: '145px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          <Box
            id="epra-logo"
            component="img"
            src={logo}
            sx={
              open
                ? { width: '60px', transition: 'all 0.25s linear', transformOrigin: 'bottom left' }
                : { width: '32px', transition: 'all 0.25s linear', transformOrigin: 'bottom left' }
            }
            alt="logo"
          />
        </Box>
        <List
          id="navigation-items"
          disablePadding={true}
          sx={{ display: 'flex', flexFlow: 'column', height: '100%' }}
        >
          <Box sx={{ flexGrow: 1 }}>
            {state.routes
              .filter((r: IRouteModel) => r.showInNav)
              .map((route: IRouteModel) => (
                <ListItemLink
                  key={route.name}
                  route={route}
                  selected={selected}
                  open={open}
                  userRoles={state.user.data?.roles}
                  handleSelectedChanged={handleSelectedChanged}
                />
              ))}
          </Box>
          <ListItem
            id="Logout-Button"
            disableGutters={true}
            key={'logout'}
            onClick={(e) => handleLogout(e)}
            button
            sx={{ flexGrow: 0, display: 'flex', justifyContent: 'center' }}
          >
            <Tooltip title={'logout'} arrow>
              <ExitToApp sx={{ color: 'common.white' }} />
            </Tooltip>
            {open && <Box sx={{ color: Colors.white, margin: '2px', fontWeight: 400 }}>Logout</Box>}
          </ListItem>
        </List>
      </Drawer>
    </Box>
  ) : null;
};

export default Navigation;
